import { useCallback, useState } from "react";
import { VisibilityState } from "@tanstack/react-table";
import { localStorageManager } from "@/services/localStorage";

/**
 * Parses a stored column-visibility map, returning null for anything that
 * is not a flat `Record<string, boolean>` — a missing key, a truncated
 * write, or a stored shape from some older version of the table. Callers
 * fall back to their own defaults on null rather than feeding a malformed
 * object into TanStack's `columnVisibility` state.
 *
 * Split out from the hook and exported so the parsing rules can be tested
 * directly: the repo's global jest localStorage mock stores nothing (its
 * `getItem` is a bare `jest.fn()`), so a round-trip test that leaned on it
 * would pass while proving nothing.
 */
export function parseStoredColumnVisibility(
  raw: string | null
): VisibilityState | null {
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (
    typeof parsed !== "object" ||
    parsed === null ||
    Array.isArray(parsed) ||
    !Object.values(parsed).every((value) => typeof value === "boolean")
  ) {
    return null;
  }

  return parsed as VisibilityState;
}

/**
 * Column-visibility persistence for a `DataTable`.
 *
 * The table itself still owns the live `columnVisibility` state — this hook
 * only supplies the initial value (read once, at mount) and a callback that
 * writes each change straight back to local storage. That keeps the choice
 * sticky without the user having to click "Save Layout," and deliberately
 * stores *only* visibility: `useUserColumns` replaces the whole stored
 * layout object, which silently drops a table's default `sorting` when the
 * table is uncontrolled.
 *
 * `key` must be one of `LOCAL_STORAGE_BASE_KEYS`' values or
 * `localStorageManager` refuses the read and the write.
 */
export function useTableColumnVisibility(
  key: string,
  fallback: VisibilityState = {}
) {
  const [initialColumnVisibility] = useState<VisibilityState>(
    () =>
      parseStoredColumnVisibility(localStorageManager.getItem(key)) ?? fallback
  );

  const persistColumnVisibility = useCallback(
    (columnVisibility: VisibilityState) => {
      localStorageManager.setItem(key, JSON.stringify(columnVisibility));
    },
    [key]
  );

  return { initialColumnVisibility, persistColumnVisibility };
}
